import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { healthCyclesAPI } from '../../services/api'
import { formatDate } from '../health-cycle/cycleStatus'

const COURSE_CLASS = {
  on_course: 'bg-green-100 text-green-800',
  off_course: 'bg-red-100 text-red-800',
  no_weight: 'bg-gray-100 text-gray-700',
}
const REASONS = ['intake_above_target', 'too_little_logging', 'at_limit']

function WeekRow({ week, label }) {
  const { t, i18n } = useTranslation()
  const kcal = (v) => Math.round(v).toLocaleString(i18n.language)
  return (
    <li className="py-2 space-y-1" data-testid={`checkpoint-${week.week_start}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="font-medium text-gray-900">{label}</span>
        <span className={`px-2 py-0.5 rounded text-xs ${COURSE_CLASS[week.course] || COURSE_CLASS.no_weight}`}>
          {t(`home.checkpoints.course.${week.course}`)}
        </span>
      </div>
      <p className="text-gray-700">
        {week.weight_avg != null
          ? t('home.checkpoints.weight', { value: week.weight_avg, expected: week.expected_weight })
          : t('home.checkpoints.weighIn', { count: week.weigh_ins })}
      </p>
      <p className="text-gray-500">
        {t('home.checkpoints.logged', { count: week.logged_days, days: week.days })}
        {week.avg_kcal != null && week.target_kcal != null && (
          <> · {t('home.checkpoints.intake', { value: kcal(week.avg_kcal), target: kcal(week.target_kcal) })}</>
        )}
        {' · '}{t('home.checkpoints.trained', { count: week.trained_days })}
      </p>
    </li>
  )
}

// VH-247 stage 3: this week and last, weight against the goal's course, and — after two weeks off
// course — a new calorie target to confirm or decline (recorded in the cycle either way).
export function CheckpointCard() {
  const { t, i18n } = useTranslation()
  const queryClient = useQueryClient()
  const [showAll, setShowAll] = useState(false)
  const [decided, setDecided] = useState(null)
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['healthCycleCheckpoints'],
    queryFn: () => healthCyclesAPI.checkpoints().then((response) => response.data),
    retry: false,
  })
  const decide = useMutation({
    mutationFn: ({ confirm, proposal }) => healthCyclesAPI.decideCheckpoint(confirm, proposal.week_start, proposal.value),
    onSuccess: (response) => {
      setDecided(response.data.decision)
      queryClient.invalidateQueries({ queryKey: ['healthCycleCheckpoints'] })
      queryClient.invalidateQueries({ queryKey: ['healthCycleState'] })
    },
    onError: () => queryClient.invalidateQueries({ queryKey: ['healthCycleCheckpoints'] }),
  })

  if (isLoading) return <div className="card"><div className="card-body text-sm text-gray-500">{t('common.loading')}</div></div>
  if (isError && error?.response?.status === 404) return null  // no agreed plan: nothing to check against
  if (isError || !data) {
    return (
      <div className="card" data-testid="checkpoints">
        <div className="card-body text-sm">
          <p className="text-red-600">{t('home.checkpoints.error')}</p>
          <button type="button" className="btn-outline mt-2 text-xs" onClick={() => refetch()}>{t('home.today.retry')}</button>
        </div>
      </div>
    )
  }

  const date = (d) => formatDate(`${d}T12:00:00`, i18n.language)
  const kcal = (v) => Math.round(v).toLocaleString(i18n.language)
  const weeks = showAll ? data.weeks : data.weeks.slice(0, 2)
  const label = (week, index) => (index === 0 && !week.complete
    ? t('home.checkpoints.thisWeek')
    : t('home.checkpoints.weekOf', { date: date(week.week_start) }))
  const proposal = data.proposal

  return (
    <section className="card" data-testid="checkpoints">
      <div className="card-body space-y-3 text-sm">
        <h2 className="text-lg font-semibold text-gray-900">{t('home.checkpoints.title')}</h2>
        {data.start_weight != null && (
          <p className="text-gray-500">
            {data.goal_weight != null
              ? t('home.checkpoints.courseLine', { start: data.start_weight, goal: data.goal_weight, rate: Math.abs(data.rate_kg_per_week) })
              : t('home.checkpoints.courseLineNoGoal', { start: data.start_weight })}
          </p>
        )}

        {proposal && (
          <div className="rounded-md bg-yellow-50 p-3 space-y-2" data-testid="calorie-proposal">
            <p className="text-yellow-900">
              {t('home.checkpoints.proposal', { from: kcal(proposal.from_value), to: kcal(proposal.value) })}
            </p>
            <div className="flex flex-wrap gap-2">
              <button type="button" className="btn-primary text-xs" disabled={decide.isPending}
                onClick={() => decide.mutate({ confirm: true, proposal })}>
                {t('home.checkpoints.confirm', { value: kcal(proposal.value) })}
              </button>
              <button type="button" className="btn-outline text-xs" disabled={decide.isPending}
                onClick={() => decide.mutate({ confirm: false, proposal })}>
                {t('home.checkpoints.decline')}
              </button>
            </div>
          </div>
        )}
        {decide.isError && <p className="text-xs text-red-600" role="alert">{t('home.checkpoints.decideError')}</p>}
        {decided && !proposal && (
          <p className="text-gray-700" role="status">{t(`home.checkpoints.decided.${decided}`)}</p>
        )}
        {!proposal && REASONS.includes(data.no_proposal_reason) && (
          <p className="text-gray-700" data-testid="no-proposal-reason">{t(`home.checkpoints.reason.${data.no_proposal_reason}`)}</p>
        )}

        {data.weeks.length === 0 ? (
          <p className="text-gray-500">{t('home.checkpoints.none')}</p>
        ) : (
          <ul className="divide-y divide-gray-100">
            {weeks.map((week, index) => <WeekRow key={week.week_start} week={week} label={label(week, index)} />)}
          </ul>
        )}
        {data.weeks.length > 2 && (
          <button type="button" className="text-sm font-medium text-primary-600 hover:text-primary-700"
            onClick={() => setShowAll((v) => !v)} aria-expanded={showAll}>
            {showAll ? t('home.checkpoints.showFewer') : t('home.checkpoints.showAll', { count: data.weeks.length })}
          </button>
        )}
      </div>
    </section>
  )
}

export default CheckpointCard
